import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { commitmentApi } from '@/services/api';
import { useAuth } from '@/context/AuthContext';

export function useGymCommitment() {
  const queryClient = useQueryClient();
  const { user, isGuestMode, loading: authLoading } = useAuth();
  
  // Memoize the enabled condition to prevent unnecessary re-renders
  const isEnabled = useMemo(() => {
    return !authLoading && (!!user || isGuestMode);
  }, [authLoading, user, isGuestMode]);
  
  // Query for fetching today's commitment status
  const {
    data: commitmentStatus,
    isLoading: loading,
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ['gym-commitment-today'],
    queryFn: async () => {
      const result = await commitmentApi.getTodayCommitment();
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error || 'Failed to fetch commitment status');
    },
    staleTime: 30 * 1000, // 30 seconds - commitment doesn't change often
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: isEnabled,
  });

  // Mutation for creating/updating commitment
  const createCommitmentMutation = useMutation({
    mutationFn: async (data: { committed: boolean }) => {
      const result = await commitmentApi.createCommitment(data);
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error || 'Failed to update commitment');
    },
    onSuccess: async () => {
      // Refresh commitment status after successful update
      await queryClient.invalidateQueries({ queryKey: ['gym-commitment-today'] });
    },
    onError: () => {
      // On error, ensure cache is refreshed
      queryClient.invalidateQueries({ queryKey: ['gym-commitment-today'] });
    },
  });

  // Helper functions
  const isCommitted = useMemo(() => {
    return commitmentStatus?.committed || false;
  }, [commitmentStatus]);

  const isCompleted = useMemo(() => {
    return commitmentStatus?.completed || false;
  }, [commitmentStatus]);

  const commitmentState = useMemo(() => {
    if (isCompleted) return 'completed';
    if (isCommitted) return 'committed';
    return 'none';
  }, [isCommitted, isCompleted]);

  const makeCommitment = useCallback(async () => {
    try {
      await createCommitmentMutation.mutateAsync({ committed: true });
      return true;
    } catch (error) {
      console.error('Failed to make commitment:', error);
      return false;
    }
  }, [createCommitmentMutation]);

  const removeCommitment = useCallback(async () => {
    try {
      await createCommitmentMutation.mutateAsync({ committed: false });
      return true;
    } catch (error) {
      console.error('Failed to remove commitment:', error);
      return false;
    }
  }, [createCommitmentMutation]);

  const error = queryError?.message || createCommitmentMutation.error?.message || null;

  return {
    commitmentStatus,
    isCommitted,
    isCompleted,
    commitmentState, // 'none' | 'committed' | 'completed'
    loading,
    error,
    makeCommitment,
    removeCommitment,
    refetch,
    isUpdating: createCommitmentMutation.isPending,
  };
}